import {
  SpwnService,
  SpwnServiceFetchPawChargeHistoryRequest,
} from "@spwn-portal/grpc";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { purchaseActions } from "modules/purchase";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";
import { useConnectClient } from "utility/connectWeb";

import type { Store } from "store";

type ChargeData = {
  [key: string]: ChargeItem; // または [key: number]: ChargeItem; より厳密には、キーは数値文字列です。
};

type ChargeItem = {
  chargeType: "MEMBER" | "TOKEN";
  createAt: number;
  freeExpire: number;
  freeValue: number;
  paidExpire: number;
  paidValue: number;
  remainingFreeValue: number;
  remainingPaidValue: number;
  status: "CHARGE";
  updateAt: number;
};

export const FetchPawChargeHistoryKey = "fetchPawChargeHistory";

export const useChargeHistory = () => {
  let end = Number.MAX_SAFE_INTEGER;
  const dispatch = useDispatch();
  const auth = firebase.auth();
  const connectClient = useConnectClient(SpwnService);
  const pawHistoryContainer = useSelector(
    (state: Store) => state.purchase.pawHistoryContainer
  ) as ChargeData;

  const reloadHistory = useCallback(() => {
    dispatch(purchaseActions.getPAWChargeHistory.started({ end }));
  }, [dispatch, end]);

  /**
   * @todo ここに新規APIの処理を追加する
   */
  const { data } = useSWR(
    auth.currentUser?.emailVerified ? FetchPawChargeHistoryKey : null,
    async () => {
      return await connectClient.fetchPawChargeHistory(
        new SpwnServiceFetchPawChargeHistoryRequest()
      );
    }
  );

  const newPawHistoryContainer = useMemo(
    () =>
      data?.data
        ?.map((history) => {
          const { chargedAt, value, validValue, expiresAt } = history;
          const status = calcStatus(Number(expiresAt), Number(validValue ?? 0));
          return {
            chargedAt: Number(chargedAt),
            value: Number(value ?? 0),
            validValue: Number(validValue ?? 0),
            expiresAt: Number(expiresAt),
            status,
          };
        })
        .sort((a, b) => {
          return -(a.chargedAt - b.chargedAt);
        }) ?? [],
    [data?.data]
  );

  if (!pawHistoryContainer || Object.keys(pawHistoryContainer).length === 0) {
    return {
      pawHistoryContainer: newPawHistoryContainer,
      reloadHistory,
    };
  }

  const oldPawHistoryContainer = Object.keys(pawHistoryContainer)
    .map((key) => {
      const {
        createAt,
        paidExpire,
        paidValue,
        remainingPaidValue: remaining,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      } = pawHistoryContainer[key]!;

      const status = calcStatus(paidExpire, remaining);

      end = end < createAt ? end : createAt;

      return {
        chargedAt: createAt,
        value: paidValue,
        validValue: remaining,
        expiresAt: paidExpire,
        status,
      };
    })
    .sort((a, b) => {
      return -(a.chargedAt - b.chargedAt);
    });

  return {
    pawHistoryContainer: newPawHistoryContainer.concat(
      oldPawHistoryContainer.map((history) => ({
        ...history,
        // 旧チャージ履歴は払い戻し対象
        status: history.status === "CHARGE" ? "REFUNDABLE" : history.status,
      }))
    ),
    reloadHistory,
  };
};

const calcStatus = (expiresAt: number, validValue: number) => {
  if (expiresAt < new Date().getTime()) {
    return "EXPIRED";
  } else if (validValue === 0) {
    return "USED";
  } else {
    return "CHARGE";
  }
};
